import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { CONDITION_OPTIONS, Condition } from '../../types/hotwheels.types';

/**
 * Form schema for create/edit. Mirrors the server-side validation
 * (Brand / Series / ModelNumber / ReleaseYear required). Numeric fields
 * are non-negative.
 */
export const hotWheelFormSchema = z.object({
  Brand: z.string().min(1, 'Brand is required').max(100),
  Series: z.string().min(1, 'Series is required').max(200),
  ModelNumber: z
    .string()
    .min(1, 'Model number is required')
    .max(50)
    .regex(/^[A-Za-z0-9-]+$/, 'Use letters, numbers, and dashes only'),
  ReleaseYear: z.coerce
    .number()
    .int('Year must be a whole number')
    .min(1968, 'Year must be 1968 or later')
    .max(new Date().getFullYear() + 1, 'Year is in the future'),
  Color: z.string().max(60).optional().or(z.literal('')),
  Scale: z.string().max(20).optional().or(z.literal('')),
  Condition: z.enum(['Carded', 'Loose', 'Opened', 'Mint']).optional().or(z.literal('')),
  PurchasePrice: z.coerce.number().min(0).optional().or(z.literal('')),
  EstimatedValue: z.coerce.number().min(0).optional().or(z.literal('')),
  PurchaseDate: z.string().optional().or(z.literal('')),
  StorageLocation: z.string().max(100).optional().or(z.literal('')),
  Description: z.string().max(2000).optional().or(z.literal('')),
});

export type HotWheelFormValues = z.infer<typeof hotWheelFormSchema>;

interface HotWheelFormFieldsProps {
  defaultValues?: Partial<HotWheelFormValues>;
  onSubmit: (values: HotWheelFormValues) => void | Promise<void>;
  isPending?: boolean;
  submitLabel?: string;
  onCancel?: () => void;
}

/**
 * All car fields except images and the boolean flags (those live alongside
 * the image uploader in the form page). The page-level form composes:
 *   <HotWheelImageUploader> (main image)
 *   <HotWheelFormFields> (text/number/select fields)
 *   <HotWheelFlags> (favorite / treasure hunt / super / limited / public)
 *   <Submit / Cancel buttons>
 */
export function HotWheelFormFields({
  defaultValues,
  onSubmit,
  isPending,
  submitLabel,
  onCancel,
}: HotWheelFormFieldsProps) {
  const { t } = useTranslation();

  const form = useForm<HotWheelFormValues>({
    resolver: zodResolver(hotWheelFormSchema),
    defaultValues: {
      Brand: '',
      Series: '',
      ModelNumber: '',
      ReleaseYear: new Date().getFullYear(),
      Color: '',
      Scale: '1:64',
      Condition: '',
      PurchasePrice: '',
      EstimatedValue: '',
      PurchaseDate: '',
      StorageLocation: '',
      Description: '',
      ...defaultValues,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="Brand"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('BRAND')}</FormLabel>
                <FormControl>
                  <Input placeholder="Hot Wheels" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Series"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('SERIES')}</FormLabel>
                <FormControl>
                  <Input placeholder="Mainline" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="ModelNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('MODEL_NUMBER')}</FormLabel>
                <FormControl>
                  <Input placeholder="HWN001" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="ReleaseYear"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('RELEASE_YEAR')}</FormLabel>
                <FormControl>
                  <Input type="number" min={1968} max={new Date().getFullYear() + 1} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Color"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('COLOR')}</FormLabel>
                <FormControl>
                  <Input placeholder="Red" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Scale"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('SCALE')}</FormLabel>
                <FormControl>
                  <Input placeholder="1:64" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Condition"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('CONDITION')}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CONDITION_OPTIONS.map((c: Condition) => (
                      <SelectItem key={c} value={c}>
                        {t(`CONDITION_${c.toUpperCase()}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="StorageLocation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('STORAGE_LOCATION')}</FormLabel>
                <FormControl>
                  <Input placeholder="Display case A" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="PurchasePrice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('PURCHASE_PRICE')}</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min={0} placeholder="1.99" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="EstimatedValue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('ESTIMATED_VALUE')}</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min={0} placeholder="5.00" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="PurchaseDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('PURCHASE_DATE')}</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="Description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('DESCRIPTION')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Notes about this car…"
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-center gap-3 justify-end pt-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md px-4 py-2 text-sm font-medium text-zinc-300 hover:bg-zinc-800"
              disabled={isPending}
            >
              {t('CANCEL')}
            </button>
          )}
          <button
            type="submit"
            disabled={isPending}
            className="rounded-md bg-[#2563EB] px-6 py-2 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-50"
          >
            {submitLabel ?? t('SAVE')}
          </button>
        </div>
      </form>
    </Form>
  );
}